/// <amd-module name="Artifacts/Root/protocols/Commands" />
import { FeedOperationPermissions } from "Artifacts/Root/Constants";
import { encodeName, encodeVersion } from "Artifacts/Root/helpers/PackageDecoderHelper";
import * as Tags from "Artifacts/Root/protocols/TagConstants";
import {
  ICommand,
  IDeleteActionPayload,
  IListActionPayload,
  IPromoteActionPayload,
  IRestoreActionPayload,
} from "Artifacts/Root/protocols/types";
import { getStore } from "Artifacts/Root/Redux";
import { RootActions } from "Artifacts/Root/Redux/Actions";
import { ICurrentModal } from "Artifacts/Root/Redux/Contracts";
import * as Res from "Artifacts/Root/Resources";
import { packageDetailsOverview } from "Artifacts/Root/routing/Constants";
import { IFeed, IPackageVersionIdentity } from "Artifacts/Root/types";
import { generatePath } from "react-router-dom";
import { ActionWithPayload } from "redux/Utils/ActionHelper";
import { announce } from "VSS/Core/Util/Accessibility";
import * as StringUtils from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import * as Utils_Url from "VSS/Platform/Util/Url";
import { copyToClipboard } from "VSSUI/Utils/ClipboardUtils";

/**
 * combineCommands combines array of commands into one command with sequential action execution.
 * Aggregated command inherits id and displayName of the first command in the array and aggregates
 * tags from other commands.
 * @param commands Array of ICommand items.
 * @returns ICommand which is an aggregated command.
 */
export function combineCommands(commands: ICommand[]): ICommand {
  if (commands.length === 0) {
    throw new Error("Cannot combine 0 commands");
  }

  if (commands.length === 1) {
    return commands[0];
  }

  const tags = commands.reduce<string[]>((acc, command) => {
    return [...acc, ...command.tags];
  }, []);

  const perms = commands.reduce<number>((acc, command) => acc | command.permissionBitMask, 0);

  return {
    id: commands[0].id,
    displayName: commands[0].displayName,
    action: function (payload?: Record<string, any>) {
      for (const command of commands) {
        command.action(payload);
      }
    },
    tags: new Set<string>(tags),
    isAnonymousAllowed: !(commands.filter((c) => !c.isAnonymousAllowed).length > 0), // disallowed as a bulk option if any command disallows it.
    permissionBitMask: perms,
  };
}

/**
 * Method that constructs a permanent delete command to be invoked on selected package versions
 * @param permanentDeleteAction Action to invoke when a permanent delete operation is submitted
 * @returns ICommand representing a permanent delete operation
 */
export function getPermanentDeleteCommandCreator<ActionT extends string, ActionPayloadT>(
  permanentDeleteAction: (
    feed: IFeed,
    versions: IPackageVersionIdentity[],
    onSuccessCallback: () => void,
  ) => ActionWithPayload<ActionT, ActionPayloadT>,
): (
  pageContext: IVssPageContext,
  versions: IPackageVersionIdentity[],
  feed: IFeed,
) => ICommand | undefined {
  return function (
    pageContext: IVssPageContext,
    versions: IPackageVersionIdentity[],
    feed: IFeed,
  ): ICommand | undefined {
    if (versions.length === 0) {
      return undefined;
    }
    const store = getStore(pageContext);
    const dispatch = store.dispatch;

    return {
      id: "permanent-delete",
      displayName: Res.PermanentDeleteCommandButton,
      action: (payload?: IDeleteActionPayload) => {
        const feed = payload && payload.feed;
        const onSuccessCallback = payload && payload.onSuccessCallback;
        if (feed === undefined || typeof feed !== "object") {
          throw new Error(StringUtils.format(Res.InvalidFeedParameter_Command, "delete"));
        }

        if (onSuccessCallback === undefined) {
          throw new Error(StringUtils.format(Res.InvalidCallbackParameter_Command, "delete"));
        }

        dispatch(permanentDeleteAction(feed, versions, onSuccessCallback));
      },
      tags: new Set<string>([Tags.RecycleBin]),
      isAnonymousAllowed: false,
      permissionBitMask: FeedOperationPermissions.DeletePackage,
    };
  };
}

/**
 * Method that constructs a restore command to be invoked on selected package versions
 * @param restoreAction Action to invoke when a restore operation is submitted
 * @returns ICommand representing a restore operation
 */
export function getRestoreCommandCreator<ActionT extends string, ActionPayloadT>(
  restoreAction: (
    feed: IFeed,
    versions: IPackageVersionIdentity[],
    onSuccessCallback: () => void,
  ) => ActionWithPayload<ActionT, ActionPayloadT>,
): (pageContext: IVssPageContext, versions: IPackageVersionIdentity[]) => ICommand | undefined {
  return function (
    pageContext: IVssPageContext,
    versions: IPackageVersionIdentity[],
  ): ICommand | undefined {
    if (versions.length === 0) {
      return undefined;
    }
    const store = getStore(pageContext);
    const dispatch = store.dispatch;

    return {
      id: "restore",
      displayName: Res.RestoreCommandButton,
      action: (payload?: IRestoreActionPayload) => {
        const feed = payload && payload.feed;
        const onSuccessCallback = payload && payload.onSuccessCallback;
        if (feed === undefined || typeof feed !== "object") {
          throw new Error(StringUtils.format(Res.InvalidFeedParameter_Command, "restore"));
        }
        if (onSuccessCallback === undefined) {
          throw new Error(StringUtils.format(Res.InvalidCallbackParameter_Command, "restore"));
        }
        dispatch(restoreAction(feed, versions, onSuccessCallback));
      },
      tags: new Set<string>([Tags.RecycleBin]),
      isAnonymousAllowed: false,
      permissionBitMask: FeedOperationPermissions.None,
    };
  };
}

/**
 * Method that constructs an upstream override command to be invoked on a selected package.
 * Not available if the EnableOverrideUI and EnableOverrideUI.MSFT feature flags are Off.
 * @param upstreamOverrideAction Action to invoke when an upstream override operation is submitted
 * @returns ICommand representing an upstream override operation
 */
export function getUpstreamOverrideCommandCreator<ActionT extends string, ActionPayloadT>(
  upstreamOverrideAction: (
    feed: IFeed,
    packageName: string,
    allowExternalVersions: boolean,
    onSuccessCallback: () => void,
  ) => ActionWithPayload<ActionT, ActionPayloadT>,
): (pageContext: IVssPageContext) => ICommand | undefined {
  return function createUpstreamOverrideCommand(
    pageContext: IVssPageContext,
  ): ICommand | undefined {
    const store = getStore(pageContext);
    const dispatch = store.dispatch;

    return {
      id: "upstream-override",
      displayName: Res.UpstreamOverride,
      action: (payload?: Record<string, any>) => {
        const feed = payload && payload.feed;
        if (feed === undefined || typeof feed !== "object") {
          throw new Error(
            StringUtils.format(Res.InvalidFeedParameter_Command, "upstream override"),
          );
        }

        const allowExternalVersions = payload && payload.allowExternalVersions;
        if (typeof allowExternalVersions !== "boolean") {
          throw new Error(
            "Invalid allowExternalVersions param provided to upstream override command.",
          );
        }
        const packageName = payload && payload.packageName;
        if (typeof packageName !== "string") {
          throw new Error("Invalid packageName param provided to upstream override command.");
        }
        const onSuccessCallback = payload
          ? payload.onSuccessCallback
          : () => {
              return;
            };
        dispatch(
          upstreamOverrideAction(feed, packageName, allowExternalVersions, onSuccessCallback),
        );
      },
      iconProps: {
        iconName: "Equalizer",
      },
      tags: new Set<string>([Tags.PackageDetails]),
      isAnonymousAllowed: false,
      permissionBitMask: FeedOperationPermissions.AdministerFeed,
    };
  };
}

/**
 * Method that constructs a (soft) delete command to be invoked on selected package versions.
 * Not available when any of the selected package versions is already deleted.
 * @param deleteAction Action to invoke when a delete operation is submitted
 * @returns undefined if a package version is deleted.
 */
export function getDeleteCommandCreator<ActionT extends string, ActionPayloadT>(
  deleteAction: (
    feed: IFeed,
    versions: IPackageVersionIdentity[],
    onSuccessCallback: () => void,
  ) => ActionWithPayload<ActionT, ActionPayloadT>,
): (
  pageContext: IVssPageContext,
  versions: IPackageVersionIdentity[],
  feed: IFeed,
) => ICommand | undefined {
  return function createDeleteCommand(
    pageContext: IVssPageContext,
    versions: IPackageVersionIdentity[],
    feed: IFeed,
  ): ICommand | undefined {
    if (versions.filter((x) => x.isDeleted).length > 0 || versions.length === 0) {
      return undefined;
    }

    const store = getStore(pageContext);
    const dispatch = store.dispatch;

    return {
      id: "delete",
      displayName: Res.DeleteCommandButton,

      action: (payload?: IDeleteActionPayload) => {
        const feed = payload && payload.feed;
        const onSuccessCallback = payload && payload.onSuccessCallback;
        if (feed === undefined || typeof feed !== "object") {
          throw new Error(StringUtils.format(Res.InvalidFeedParameter_Command, "delete"));
        }

        if (onSuccessCallback === undefined) {
          throw new Error(StringUtils.format(Res.InvalidCallbackParameter_Command, "delete"));
        }

        dispatch(deleteAction(feed, versions, onSuccessCallback));
      },
      iconProps: {
        iconName: "Cancel",
      },
      tags: new Set<string>([Tags.PackageList, Tags.PackageDetails]),
      isAnonymousAllowed: false,
      permissionBitMask: FeedOperationPermissions.DeletePackage,
    };
  };
}

/**
 * Method that constructs a download command to be invoked on selected package versions.
 * Not available when any of the selected package versions is deleted.
 * @param downloadAction Action to invoke when a download operation is submitted
 * @returns undefined if a package version is deleted.
 */
export function getDownloadCommandCreator<ActionT extends string, ActionPayloadT>(
  downloadAction: (
    feed: IFeed,
    versions: IPackageVersionIdentity[],
  ) => ActionWithPayload<ActionT, ActionPayloadT>,
): (
  pageContext: IVssPageContext,
  versions: IPackageVersionIdentity[],
  feed: IFeed,
) => ICommand | undefined {
  return function createDownloadCommand(
    pageContext: IVssPageContext,
    versions: IPackageVersionIdentity[],
    feed: IFeed,
  ): ICommand | undefined {
    if (versions.filter((x) => x.isDeleted).length > 0 || versions.length === 0) {
      return undefined;
    }
    const store = getStore(pageContext);
    const dispatch = store.dispatch;

    return {
      id: "download",
      displayName: Res.DownloadCommandButton,
      action: () => {
        dispatch(downloadAction(feed, versions));
      },
      iconProps: {
        iconName: "Download",
      },
      tags: new Set<string>([Tags.PackageList, Tags.PackageDetails]),
      isAnonymousAllowed: true,
      permissionBitMask: FeedOperationPermissions.None,
    };
  };
}

/**
 * Method that constructs a promote command to be invoked on selected package versions.
 * Not available when any of the selected package versions is deleted.
 * @param promoteAction Action to invoke when a promote operation is submitted
 * @returns undefined if a package version is deleted.
 */
export function getPromoteCommandCreator<ActionT extends string, ActionPayloadT>(
  promoteAction: (
    feed: IFeed,
    versions: IPackageVersionIdentity[],
    viewId: string,
    onSuccessCallback: () => void,
  ) => ActionWithPayload<ActionT, ActionPayloadT>,
): (pageContext: IVssPageContext, versions: IPackageVersionIdentity[]) => ICommand | undefined {
  return function createPromoteCommand(
    pageContext: IVssPageContext,
    versions: IPackageVersionIdentity[],
  ): ICommand | undefined {
    if (versions.filter((x) => x.isDeleted).length > 0 || versions.length === 0) {
      return undefined;
    }
    const store = getStore(pageContext);
    const dispatch = store.dispatch;

    return {
      id: "promote",
      displayName: Res.PromoteCommandButton,
      action: (payload?: IPromoteActionPayload) => {
        const viewId = payload && payload.viewId;
        const feed = payload && payload.feed;
        const onSuccessCallback = payload && payload.onSuccessCallback;

        if (typeof viewId !== "string") {
          throw new Error(StringUtils.format(Res.InvalidViewParameter_Command, "promote"));
        }
        if (feed === undefined || typeof feed !== "object") {
          throw new Error(StringUtils.format(Res.InvalidFeedParameter_Command, "promote"));
        }
        if (onSuccessCallback === undefined) {
          throw new Error(StringUtils.format(Res.InvalidCallbackParameter_Command, "promote"));
        }

        dispatch(promoteAction(feed, versions, viewId, onSuccessCallback));
      },
      iconProps: {
        iconName: "Up",
      },
      tags: new Set<string>([Tags.PackageList, Tags.PackageDetails]),
      isAnonymousAllowed: false,
      permissionBitMask: FeedOperationPermissions.UpdatePackage,
    };
  };
}

/**
 * Method that constructs a create badge command to be invoked on a single selected package version.
 * Not available when the selected package version is deleted.
 * Not available if packages badges isn't enabled.
 * @returns undefined if the package version is deleted,
 * if more than one version is selected, or if package badges aren't enabled.
 */
export function getBadgeCommandCreator<ActionT extends string, ActionPayloadT>(): (
  pageContext: IVssPageContext,
  versions: IPackageVersionIdentity[],
  feed: IFeed,
) => ICommand | undefined {
  return function createBadgeCommand(
    pageContext: IVssPageContext,
    versions: IPackageVersionIdentity[],
    feed: IFeed,
  ): ICommand | undefined {
    if (
      versions.length !== 1 ||
      !feed.isBadgesEnabled ||
      versions.filter((x) => x.isDeleted).length > 0
    ) {
      return undefined;
    }

    return {
      id: "package-badge",
      displayName: Res.CreateBadgeCommandButton,
      action: () => {
        null;
      },
      iconProps: {
        iconName: "Share",
      },
      tags: new Set<string>([Tags.PackageDetails]),
      isAnonymousAllowed: true,
      permissionBitMask: FeedOperationPermissions.ReadPackages,
    };
  };
}

/**
 * Method that constructs a 'get link' command to be invoked on a single selected package version.
 * The command copies a link to the version's package details page to clipboard.
 * @returns undefined if more than one version is selected
 */
export function getLinkCommandCreator<ActionT extends string, ActionPayloadT>(): (
  pageContext: IVssPageContext,
  versions: IPackageVersionIdentity[],
  feed: IFeed,
) => ICommand | undefined {
  return function createGetLinkCommand(
    pageContext: IVssPageContext,
    versions: IPackageVersionIdentity[],
    feed: IFeed,
  ): ICommand | undefined {
    if (versions.length !== 1) {
      return undefined;
    }
    const version = versions[0];
    const currentUri = new Utils_Url.Uri(window.location.href);
    const artifactsUrlPart = "_artifacts";
    const endOfArtifactsIndex =
      currentUri.absoluteUri.indexOf(artifactsUrlPart) + artifactsUrlPart.length;
    const artifactsBasePath = currentUri.absoluteUri.substring(0, endOfArtifactsIndex);

    return {
      id: "get-link",
      displayName: Res.GetLinkCommandButton,
      action: () => {
        const relativePathToVersion = generatePath(packageDetailsOverview, {
          feedIdentifier: feed.name,
          protocol: version.protocol.toString(),
          packageName: encodeName(version.name, version.protocol.toString()),
          version: encodeVersion(version.version, version.protocol.toString()),
        });
        copyToClipboard(artifactsBasePath + relativePathToVersion, () => announce(Res.Copied));
      },
      iconProps: {
        iconName: "Copy",
      },
      tags: new Set<string>([Tags.PackageDetails]),
      isAnonymousAllowed: true,
      permissionBitMask: FeedOperationPermissions.ReadPackages,
    };
  };
}

/**
 * Method that constructs an unlist command to be invoked on selected package versions.
 * Not available when any of the selected package versions is deleted.
 * Only available for nuget protocols
 * @param unlistAction Action to invoke when an unlist operation is submitted
 * @returns undefined if a package version is deleted.
 */
export function getUnlistCommandCreator<ActionT extends string, ActionPayloadT>(
  changeListStatusAction: (
    feed: IFeed,
    versions: IPackageVersionIdentity[],
    isListed: boolean,
    onSuccessCallback: () => void,
  ) => ActionWithPayload<ActionT, ActionPayloadT>,
): (
  pageContext: IVssPageContext,
  versions: IPackageVersionIdentity[],
  feed: IFeed,
) => ICommand | undefined {
  return unlistYankHelper(
    changeListStatusAction,
    "unlistRelist",
    Res.UnlistCommand,
    Res.RelistCommand,
  );
}

/**
 * Method that constructs a yank command to be invoked on selected package versions.
 * Not available when any of the selected package versions is deleted.
 * Only available for cargo protocols
 * @param unlistAction Action to invoke when a yank operation is submitted
 * @returns undefined if a package version is deleted.
 */
export function getYankCommandCreator<ActionT extends string, ActionPayloadT>(
  changeListStatusAction: (
    feed: IFeed,
    versions: IPackageVersionIdentity[],
    isListed: boolean,
    onSuccessCallback: () => void,
  ) => ActionWithPayload<ActionT, ActionPayloadT>,
): (
  pageContext: IVssPageContext,
  versions: IPackageVersionIdentity[],
  feed: IFeed,
) => ICommand | undefined {
  return unlistYankHelper(changeListStatusAction, "unyankYank", Res.YankCommand, Res.UnyankCommand);
}

function unlistYankHelper<ActionT extends string, ActionPayloadT>(
  changeListStatusAction: (
    feed: IFeed,
    versions: IPackageVersionIdentity[],
    isListed: boolean,
    onSuccessCallback: () => void,
  ) => ActionWithPayload<ActionT, ActionPayloadT>,
  commandId: string,
  unlistCommand: string,
  relistCommand: string,
) {
  return function createUnlistCommand(
    pageContext: IVssPageContext,
    versions: IPackageVersionIdentity[],
  ): ICommand | undefined {
    if (versions.filter((x) => x.isDeleted).length > 0 || versions.length === 0) {
      return undefined;
    }
    const store = getStore(pageContext);
    const dispatch = store.dispatch;

    // Had to do this so that generic contracts did not have to deal with protocol specific.

    // What is in the store is the guid id of the version and the display of the version. BUT we get passed in the package's name + the display version.
    // Display versions are not unique to specific packages. We'd have to go into multiple parts of the state to even be able to check uniqueness.
    // SOLUTION: I'm just adding isListed to the IPackageVersionIdentity.

    // When multiple versions are selected and some are unlisted some are listed,
    // if there is even one version that is listed, we unlist everything.
    // False will mean unlist everything, true will mean relist.
    const isListedValueToServer = !(versions.filter((v) => v.isListed === true).length > 0);

    return {
      id: commandId,
      displayName: isListedValueToServer ? relistCommand : unlistCommand,
      action: (payload?: IListActionPayload) => {
        const feed = payload && payload.feed;
        const onSuccessCallback = payload && payload.onSuccessCallback;
        if (feed === undefined || typeof feed !== "object") {
          throw new Error(StringUtils.format(Res.InvalidFeedParameter_Command, unlistCommand));
        }
        if (onSuccessCallback === undefined) {
          throw new Error(StringUtils.format(Res.InvalidCallbackParameter_Command, unlistCommand));
        }

        dispatch(changeListStatusAction(feed, versions, isListedValueToServer, onSuccessCallback));
      },
      iconProps: {
        iconName: isListedValueToServer ? "CheckMark" : "SkypeMinus",
      },
      tags: new Set<string>([Tags.PackageList, Tags.PackageDetails]),
      isAnonymousAllowed: false,
      permissionBitMask: FeedOperationPermissions.DelistPackage,
    };
  };
}

/**
 * injectModal creates function that replaces protocol command with provided ID by command that shows
 * a modal dialog that fires original command on submit. Injector function attempts to inject modal command for
 * a target command to command array and return new array with modified target command.
 * If target command is not found in a provided array, same array will be returned.
 *
 * @param pageContext VSS page context.
 * @param commandId ID of a target command for which modal will be injected
 * @param modalId ID of a modal that will be injected.
 * @param modalPropsCreator props creator function. It accepts target command and hideModal action as parameters.
 * @returns function that accepts array of commands and return array of commands with injected modal for a target command.
 */
export function injectModal<ModalPropsT>(
  pageContext: IVssPageContext,
  commandId: string,
  modalId: string,
  modalPropsCreator: (command: ICommand, hideModal: () => void) => ModalPropsT,
): (commands: ICommand[]) => ICommand[] {
  return (commands: ICommand[]) => {
    const store = getStore(pageContext);
    const dispatch = store.dispatch;
    const commandIndex = commands.findIndex((command) => command.id === commandId);
    if (commandIndex === -1) {
      return commands;
    }

    const command = commands[commandIndex];

    const hideModal = () => dispatch(RootActions.hideModal());

    commands = [
      ...commands.slice(0, commandIndex),
      {
        id: commandId,
        displayName: command.displayName,
        action: () => {
          const modalPayload: ICurrentModal<ModalPropsT> = {
            modalId: modalId,
            props: modalPropsCreator(command, hideModal),
          };
          dispatch(RootActions.showModal(modalPayload));
        },
        tags: new Set(command.tags),
        iconProps: command.iconProps,
        isAnonymousAllowed: command.isAnonymousAllowed,
        permissionBitMask: command.permissionBitMask,
      },
      ...commands.slice(commandIndex + 1),
    ];

    return commands;
  };
}
